/**
 * Formata uma data para exibição relativa
 *
 * @param dateString - Data em formato ISO string
 * @returns String formatada ("há 2 minutos", "ontem", "hoje", "02/10/2025")
 *
 * @example
 * formatRelativeDate('2024-10-04T10:30:00Z') // "há 2 minutos"
 * formatRelativeDate('2024-10-03T10:30:00Z') // "ontem"
 * formatRelativeDate('2024-09-20T10:30:00Z') // "20/09/2024"
 */
export function formatRelativeDate(dateString: string | undefined): string {
  if (!dateString) return '---';

  const now = new Date();
  const messageDate = new Date(dateString);

  // Verificar se é uma data válida
  if (isNaN(messageDate.getTime())) {
    return '---';
  }

  const diffMs = now.getTime() - messageDate.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  // Calcular diferença de dias de forma mais precisa
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDate = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
  const diffDays = Math.floor((nowDate.getTime() - msgDate.getTime()) / 86400000);

  // Agora (menos de 1 minuto)
  if (diffMinutes < 1) return 'agora';

  // Minutos atrás
  if (diffMinutes < 60) {
    return `há ${diffMinutes}min`;
  }

  // Horas atrás (menos de 24h e mesmo dia)
  if (diffHours < 24 && diffDays === 0) {
    return `há ${diffHours}h`;
  }

  // Hoje (mesmo dia mas há mais de 24h atrás - caso de virada do dia)
  if (diffDays === 0) {
    return messageDate.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Ontem
  if (diffDays === 1) return 'ontem';

  // Menos de 7 dias
  if (diffDays < 7) {
    const dias = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
    return dias[messageDate.getDay()];
  }

  // Data compacta (DD/MM/AA) — coluna de hora da lista é estreita
  return messageDate.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
}

/**
 * Extrai a primeira letra de uma string para usar como inicial de avatar
 *
 * @param text - Texto para extrair a inicial
 * @returns Primeira letra em maiúscula ou 'C' como fallback
 */
export function getInitials(text: string | undefined): string {
  if (!text || text.trim().length === 0) return 'C';
  return text.trim().charAt(0).toUpperCase();
}

/**
 * Trunca texto longo para exibição
 *
 * @param text - Texto para truncar
 * @param maxLength - Tamanho máximo (default: 50)
 * @returns Texto truncado com "..." se necessário
 */
export function truncateText(text: string | undefined, maxLength: number = 50): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}
